import React, {useCallback, useEffect, useState} from 'react';

import {CopyButton} from 'src/components/copy-button';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {getResourcesByIds} from 'src/openapi/admin/apis/EntitlementsResourceApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {PaginationStripe} from '@pb/kit-component-pagination';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';


import {AddResourceSuggest} from './add-resource-suggest';

import type {EntitlementResource} from 'src/openapi/admin/models/EntitlementResource';
import type {Pagination} from 'src/openapi/admin/models/Pagination';

type Props = {
    resourceIds: string[],
    onAddResource: (resourceId: string) => void,
    onRemoveResource: (resourceId: string) => void,
};

export const GrantResources = ({onAddResource, onRemoveResource, resourceIds}: Props) => {
    const apiDispatch = useAppDispatch();

    const [isFetching, setIsFetching] = useState(true);
    const [{currentPage, pageSize}, setPagination] = useState<Pagination>({currentPage: 1, pageSize: 5});
    const [displayedResources, setDisplayedResources] = useState<Record<string, EntitlementResource>>({});

    const loadResources = useCallback(async (requestBody: string[]) => {
        setIsFetching(true);
        const resourcesResponse = await apiDispatch(getResourcesByIds({requestBody}));
        if (resourcesResponse.payload.success) {
            setDisplayedResources(resourcesResponse.payload.data);
        }
        setIsFetching(false);
    }, [apiDispatch]);

    useEffect(() => {
        loadResources(resourceIds);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            loadResources(current);
        }
    }, resourceIds);

    const removeResource = useCallback((resourceId: string) => {
        const totalPages = Math.ceil((resourceIds.length - 1) / pageSize);

        if ((resourceIds.length - 1) && currentPage > totalPages) {
            setPagination({currentPage: totalPages, pageSize});
        }

        onRemoveResource(resourceId);
    }, [resourceIds, pageSize, currentPage, onRemoveResource]);

    return (
        <FormSection headline="Resource(s) in this Grant">
            <Group direction="v" alignItems="normal" gap="m">
                <AddResourceSuggest
                    id="add-resource-suggest"
                    onSearch={onAddResource}
                    selectedResources={resourceIds}
                />
                {resourceIds.length ? (
                    <Item style={{width: 600}}>
                        <BoxLoading loading={isFetching}>
                            <Group direction="v">
                                <Table borderedGrid={true}>
                                    <Head>
                                        <Cell width={200}>Name</Cell>
                                        <Cell>Description</Cell>
                                        <Cell width={200}>Resource ID</Cell>
                                        <Cell width={45}/>
                                    </Head>
                                    <Body>
                                        {resourceIds
                                            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                                            .map(resourceId => {
                                                const name = displayedResources[resourceId]?.name || '--';
                                                const description = displayedResources[resourceId]?.description || '--';

                                                return (
                                                    <Row key={`${resourceId}_${name}_${description}`}>
                                                        <Cell>
                                                            <EllipsisTextHint title={name}>
                                                                {name}
                                                            </EllipsisTextHint>
                                                        </Cell>
                                                        <Cell>
                                                            <EllipsisTextHint title={description}>
                                                                {description}
                                                            </EllipsisTextHint>
                                                        </Cell>
                                                        <Cell>
                                                            <Group gap="s" alignItems="center" justify="space-between">
                                                                <Item style={{width: 125}}>
                                                                    <EllipsisTextHint title={resourceId}>
                                                                        {resourceId}
                                                                    </EllipsisTextHint>
                                                                </Item>
                                                                <CopyButton
                                                                    copiedText={resourceId}
                                                                    entityName="Resource ID"
                                                                />
                                                            </Group>
                                                        </Cell>
                                                        <Cell>
                                                            <BasicIconButton
                                                                iconProps={{type: Icons.TRASH_LIGHT}}
                                                                onClick={() => removeResource(resourceId)}
                                                                title="Remove Resource"
                                                            />
                                                        </Cell>
                                                    </Row>
                                                );
                                            })}
                                    </Body>
                                </Table>
                                <PaginationStripe
                                    totalCount={resourceIds.length}
                                    currentPage={currentPage}
                                    pageSize={pageSize}
                                    onPaginationChange={setPagination}
                                    sizes={[5, 10]}
                                />
                            </Group>
                        </BoxLoading>
                    </Item>
                ) : null}
            </Group>
        </FormSection>
    );
};
